/**
 * Input Sanitization Utilities - XSS Prevention
 * 
 * Provides functions to sanitize user input before storage/display
 * to prevent Cross-Site Scripting (XSS) attacks.
 * 
 * Follows code-quality.md standards:
 * - Pure functions
 * - Immutability
 * - Small, focused functions
 * - Composition over nesting
 */

// HTML entities that need escaping for XSS prevention
const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;'
};

// Fields that commonly contain user-editable text in orders
const ORDER_TEXT_FIELDS = [
  'customerName',
  'address',
  'streetAddress',
  'neighborhood',
  'city',
  'state',
  'complement',
  'reference',
  'items',
  'paymentMethod',
  'salesChannel',
  'manualRouteGroup'
] as const;

type OrderTextField = typeof ORDER_TEXT_FIELDS[number];

/**
 * Escape HTML entities in a string to prevent XSS
 * Pure function - no side effects
 * 
 * @param input - Raw user input string
 * @returns Sanitized string with HTML entities escaped
 */
export function sanitizeString(input: string): string {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .split('')
    .map(char => HTML_ENTITIES[char] || char)
    .join('');
}

/**
 * Check if a field name is a known order text field
 * Pure function
 * 
 * @param fieldName - Field name to check
 * @returns True if field contains user-editable text
 */
export function isOrderTextField(fieldName: string): fieldName is OrderTextField {
  return (ORDER_TEXT_FIELDS as readonly string[]).includes(fieldName);
}

/**
 * Sanitize a single field value based on field name
 * Pure function
 * 
 * @param value - Field value to sanitize
 * @param fieldName - Name of the field
 * @returns Sanitized value
 */
function sanitizeField(value: unknown, fieldName: string): unknown {
  if (value === null || value === undefined) return value;
  
  // Only sanitize string/text fields
  if (isOrderTextField(fieldName) && typeof value === 'string') {
    return sanitizeString(value);
  }
  
  return value;
}

/**
 * Deep sanitize an object's text fields
 * Uses recursion for nested objects
 * Pure function - creates new object
 * 
 * @param obj - Object to sanitize
 * @returns New object with sanitized text fields
 */
export function sanitizeObject<T extends object>(obj: T): T {
  if (!obj || typeof obj !== 'object') return obj;
  
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeObject(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      // Sanitize array of strings or objects
      sanitized[key] = value.map((item, index) => {
        if (typeof item === 'string') {
          return isOrderTextField(key) ? sanitizeString(item) : item;
        }
        if (item && typeof item === 'object') {
          // For arrays of objects, sanitize string fields
          return sanitizeField(item, key) || item;
        }
        return item;
      });
    } else {
      // Sanitize individual field
      sanitized[key] = sanitizeField(value, key);
    }
  }
  
  return sanitized as T;
}

/**
 * Order input interface for type safety
 */
export interface OrderInput {
  customerName?: string;
  address?: string;
  streetAddress?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  complement?: string;
  reference?: string;
  items?: string;
  paymentMethod?: string;
  salesChannel?: string;
  manualRouteGroup?: string;
  [key: string]: unknown;
}

/**
 * Sanitized order output type
 */
export type SanitizedOrder = OrderInput;

/**
 * Sanitize order data before storage/display
 * Pure function - creates new object
 * 
 * @param order - Raw order input
 * @returns New order object with sanitized text fields
 */
export function sanitizeOrderData(order: OrderInput): SanitizedOrder {
  return sanitizeObject(order);
}

/**
 * Sanitize user inputs from forms
 * Pure function
 * 
 * @param input - Raw form input string
 * @returns Sanitized string safe for storage/display
 */
export function sanitizeFormInput(input: string): string {
  return sanitizeString(input);
}

/**
 * Remove undefined values from object (for JSON/Supabase compatibility)
 * Also applies sanitization to text fields
 * Pure function - creates new object
 * 
 * @param data - Object to clean and sanitize
 * @returns New clean object without undefined values
 */
export function cleanAndSanitize<T extends object>(data: T): T {
  const sanitized = sanitizeObject(data);
  const clean: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(sanitized)) {
    if (value !== undefined) {
      clean[key] = value;
    }
  }
  
  return clean as T;
}
